import React, { useEffect, useMemo, useState } from 'react';
import { Layers, Sparkles } from 'lucide-react';
import { GAME_MODE_CATALOG, type GameMode } from '@interactive-room';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/primitives/Dialog';
import { MODE_VISUALS } from '../../theme/designSystem';
import { EXAM_LEVEL_OPTIONS, type ExamLevelId } from '../constants/examAiParams';
import {
  RECIPE_DIFFICULTY_OPTIONS,
  type RecipeDifficultyId,
} from '../constants/recipeAiParams';
import { distributeModesAcrossMoments } from '../lib/multiToolActivityGeneration';

export interface MultiToolActivityDialogResult {
  modes: GameMode[];
  momentCount: number;
  level: ExamLevelId;
  difficulty: RecipeDifficultyId;
  tuning: string;
}

interface MultiToolActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjectLabel: string;
  availableModes: GameMode[];
  courseLevel?: ExamLevelId | '';
  loading?: boolean;
  onConfirm: (result: MultiToolActivityDialogResult) => void;
}

const MOMENT_OPTIONS = [2, 3, 4, 5, 6, 8] as const;

export function MultiToolActivityDialog({
  open,
  onOpenChange,
  subjectLabel,
  availableModes,
  courseLevel = '',
  loading = false,
  onConfirm,
}: MultiToolActivityDialogProps) {
  const [selectedModes, setSelectedModes] = useState<GameMode[]>([]);
  const [momentCount, setMomentCount] = useState<number>(4);
  const [level, setLevel] = useState<ExamLevelId | ''>(courseLevel || '');
  const [difficulty, setDifficulty] = useState<RecipeDifficultyId>('medium');
  const [tuning, setTuning] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedModes(availableModes.slice(0, Math.min(3, availableModes.length)));
    setMomentCount(4);
    setLevel(courseLevel || '');
    setDifficulty('medium');
    setTuning('');
  }, [open, availableModes, courseLevel]);

  const preview = useMemo(
    () => distributeModesAcrossMoments(selectedModes, momentCount),
    [selectedModes, momentCount],
  );

  const canSubmit = !loading && selectedModes.length > 0 && Boolean(level);

  const toggleMode = (mode: GameMode) => {
    setSelectedModes((prev) =>
      prev.includes(mode) ? prev.filter((item) => item !== mode) : [...prev, mode],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-create-modal flex max-h-[min(92dvh,40rem)] w-[min(calc(100vw-1.5rem),34rem)] flex-col overflow-hidden rounded-studio-2xl border-0 p-0 shadow-studio-lg">
        <div className="studio-create-modal__scrim shrink-0 border-b border-studio-border px-6 pb-4 pt-6">
          <DialogHeader className="space-y-1 text-left">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-studio-ink-tertiary">
              Multi-herramienta · {subjectLabel}
            </p>
            <DialogTitle className="font-display text-xl text-studio-ink">
              Armar una clase con varios motores
            </DialogTitle>
            <DialogDescription className="text-sm text-studio-ink-secondary">
              Elegí las herramientas y cuántos momentos tendrá. La IA reparte y genera una clase
              interactiva lista para editar.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div>
            <p className="text-xs font-medium text-studio-ink-secondary">Herramientas del kit</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {availableModes.map((mode) => {
                const meta = GAME_MODE_CATALOG[mode];
                const visual = MODE_VISUALS[mode];
                const selected = selectedModes.includes(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={selected}
                    className={`multi-tool-chip${selected ? ' multi-tool-chip--selected' : ''}`}
                    style={{ ['--mode-hue' as string]: visual.hue }}
                    onClick={() => toggleMode(mode)}
                  >
                    <span aria-hidden>{meta.icon}</span>
                    {meta.displayName}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-studio-ink-secondary">Cantidad de momentos</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {MOMENT_OPTIONS.map((count) => (
                <button
                  key={count}
                  type="button"
                  aria-pressed={momentCount === count}
                  className={`multi-tool-chip${momentCount === count ? ' multi-tool-chip--selected' : ''}`}
                  onClick={() => setMomentCount(count)}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          {preview.length > 0 ? (
            <div className="rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-studio-ink-tertiary">
                Secuencia prevista
              </p>
              <ol className="mt-2 flex flex-wrap gap-1.5">
                {preview.map((mode, index) => (
                  <li key={`${mode}-${index}`} className="multi-tool-sequence-item">
                    <span className="multi-tool-sequence-item__n">{index + 1}</span>
                    {GAME_MODE_CATALOG[mode]?.displayName ?? mode}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <label className="block text-xs font-medium text-studio-ink-secondary">
            Nivel educativo
            <select
              className="studio-create-field mt-1.5 w-full rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2.5 text-sm text-studio-ink"
              value={level}
              onChange={(event) => setLevel(event.target.value as ExamLevelId | '')}
            >
              <option value="">Selecciona nivel…</option>
              {EXAM_LEVEL_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-medium text-studio-ink-secondary">
            Dificultad
            <select
              className="studio-create-field mt-1.5 w-full rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2.5 text-sm text-studio-ink"
              value={difficulty}
              onChange={(event) => setDifficulty(event.target.value as RecipeDifficultyId)}
            >
              {RECIPE_DIFFICULTY_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-medium text-studio-ink-secondary">
            Tema / intención (opcional)
            <textarea
              className="studio-create-field mt-1.5 min-h-[5.5rem] w-full resize-y rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-3 text-sm text-studio-ink"
              placeholder="Ej.: Repaso de fracciones equivalentes con activación, práctica y cierre…"
              value={tuning}
              onChange={(event) => setTuning(event.target.value)}
            />
          </label>
        </div>

        <DialogFooter className="flex shrink-0 items-center justify-between gap-2 border-t border-studio-border bg-studio-surface-muted/50 px-6 py-4 sm:justify-between">
          <button
            type="button"
            className="studio-soft-btn-outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="studio-create-modal__btn-primary inline-flex items-center"
            disabled={!canSubmit}
            onClick={() => {
              if (!level || selectedModes.length === 0) return;
              onConfirm({
                modes: selectedModes,
                momentCount,
                level,
                difficulty,
                tuning: tuning.trim(),
              });
            }}
          >
            {loading ? (
              'Generando…'
            ) : (
              <>
                <Sparkles className="mr-1.5 h-4 w-4" aria-hidden />
                Generar clase
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MultiToolActivityCta({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="multi-tool-cta" onClick={onClick}>
      <span className="multi-tool-cta__icon" aria-hidden>
        <Layers className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block font-display text-base text-[var(--sala-ink)]">
          Actividad multi-herramienta
        </span>
        <span className="mt-0.5 block text-sm text-[var(--sala-ink-muted)]">
          Combiná varios motores en una sola clase con N momentos.
        </span>
      </span>
      <span className="multi-tool-cta__action">Crear</span>
    </button>
  );
}
